import React, { useState } from 'react';
import { Send, MapPin, CalendarDays, BellRing, MessageSquare, TrendingUp, Leaf, AlertTriangle, Lightbulb, Sparkles, Filter } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Legend } from 'recharts';
import { CollectesHeatMap, collectesDistricts, getAdoptionStatus } from './CollectesHeatMap';
// --- MOCK DATA FOR CHARTS ---
const monthlyData = [{
  name: 'Jan',
  downloads: 8200,
  uniqueDownloads: 5100,
  displays: 28000,
  reminders: 14200,
  uniqueReminders: 11000,
  questions: 2100,
  posFeedback: 1800,
  negFeedback: 300
}, {
  name: 'Fév',
  downloads: 7800,
  uniqueDownloads: 4900,
  displays: 26500,
  reminders: 14800,
  uniqueReminders: 11500,
  questions: 1950,
  posFeedback: 1700,
  negFeedback: 250
}, {
  name: 'Mar',
  downloads: 9100,
  uniqueDownloads: 5800,
  displays: 31000,
  reminders: 15500,
  uniqueReminders: 12100,
  questions: 2400,
  posFeedback: 2100,
  negFeedback: 300
}, {
  name: 'Avr',
  downloads: 11500,
  uniqueDownloads: 7200,
  displays: 38500,
  reminders: 16800,
  uniqueReminders: 13200,
  questions: 3100,
  posFeedback: 2800,
  negFeedback: 300
}, {
  name: 'Mai',
  downloads: 10800,
  uniqueDownloads: 6900,
  displays: 36000,
  reminders: 17900,
  uniqueReminders: 14000,
  questions: 2800,
  posFeedback: 2550,
  negFeedback: 250
}, {
  name: 'Juin',
  downloads: 12842,
  uniqueDownloads: 8421,
  displays: 42680,
  reminders: 19204,
  uniqueReminders: 14882,
  questions: 3418,
  posFeedback: 3144,
  negFeedback: 274
}];
const envData = [{
  name: 'Jan',
  dechets: 1200,
  recyclage: 850,
  compost: 400
}, {
  name: 'Fév',
  dechets: 1150,
  recyclage: 820,
  compost: 380
}, {
  name: 'Mar',
  dechets: 1250,
  recyclage: 900,
  compost: 450
}, {
  name: 'Avr',
  dechets: 1300,
  recyclage: 950,
  compost: 600
}, {
  name: 'Mai',
  dechets: 1350,
  recyclage: 1000,
  compost: 850
}, {
  name: 'Juin',
  dechets: 1400,
  recyclage: 1050,
  compost: 1100
}];
const perfData = [{
  name: 'Déchets',
  reussite: 99.2
}, {
  name: 'Recyclage',
  reussite: 98.8
}, {
  name: 'Compost',
  reussite: 97.5
}, {
  name: 'Encombrants',
  reussite: 94.2
}, {
  name: 'Branches',
  reussite: 91.8
}];
export function CollectesAnalytics() {
  const [selectedDistrictId, setSelectedDistrictId] = useState<string>(collectesDistricts[1].id); // Default to B
  const selectedDistrict = collectesDistricts.find(d => d.id === selectedDistrictId) ?? collectesDistricts[0];
  const getStatusBadge = (status: 'critical' | 'warning' | 'on-track') => {
    if (status === 'critical') return 'bg-red-50 text-red-700 border-red-200';
    if (status === 'warning') return 'bg-amber-50 text-amber-700 border-amber-200';
    return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  };
  const getStatusLabel = (status: 'critical' | 'warning' | 'on-track') => {
    if (status === 'critical') return 'Faible adoption';
    if (status === 'warning') return 'Adoption moyenne';
    return 'Forte adoption';
  };
  return <div className="space-y-10 animate-in fade-in duration-300 pb-20" data-id="element-1147" data-mp="2ommic2xwckng">
      {/* HEADER */}
      <section className="flex flex-col gap-2" data-id="element-1148" data-mp="2tlpo5plpauwh">
        <h2 className="text-2xl font-bold text-[#0F172A] font-heading" data-id="element-1149" data-mp="2tmimc26lgc30">
          Collectes — Analytiques
        </h2>
        <p className="text-slate-500 text-base font-body max-w-3xl" data-id="element-1150" data-mp="2fnsp8mjrycen">
          Mesurez l’usage citoyen, la performance des rappels et les
          opportunités d’amélioration.
        </p>
      </section>

      {/* SECTION 1 — KPI EXÉCUTIFS */}
      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-1151" data-mp="itedodtnc7fq">
        <div className="border border-slate-200 rounded-xl shadow-sm p-5 flex flex-col gap-2 bg-white" data-id="element-1152" data-mp="irbo1uweogbj">
          <div className="text-sm font-medium text-slate-500 font-body" data-id="element-1153" data-mp="29xq0yrnd0929">
            Calendriers téléchargés
          </div>
          <div className="text-3xl font-bold text-[#0F172A] font-heading" data-id="element-1154" data-mp="24xvbmsj8frjb">
            12 842
          </div>
          <div className="text-sm text-emerald-600 font-medium font-body" data-id="element-1155" data-mp="20vzzsu3odvra">
            +18 % vs mois précédent
          </div>
        </div>
        <div className="border border-slate-200 rounded-xl shadow-sm p-5 flex flex-col gap-2 bg-white" data-id="element-1156" data-mp="1w01fpipdqzwo">
          <div className="text-sm font-medium text-slate-500 font-body" data-id="element-1157" data-mp="1r55fhv9zp68h">
            Adresses uniques (téléchargement)
          </div>
          <div className="text-3xl font-bold text-[#0F172A] font-heading" data-id="element-1158" data-mp="1h9kfg2oheg1d">
            8 421
          </div>
          <div className="text-sm text-slate-500 font-body" data-id="element-1159" data-mp="1d7qk89x2248l">
            66 % des téléchargements
          </div>
        </div>
        <div className="border border-slate-200 rounded-xl shadow-sm p-5 flex flex-col gap-2 bg-white" data-id="element-1160" data-mp="188w2zceqknql">
          <div className="text-sm font-medium text-slate-500 font-body" data-id="element-1161" data-mp="3jv7r7gpc7qtp">
            Calendriers consultés
          </div>
          <div className="text-3xl font-bold text-[#0F172A] font-heading" data-id="element-1162" data-mp="3own94zw7xh7v">
            42 680
          </div>
          <div className="text-sm text-emerald-600 font-medium font-body" data-id="element-1163" data-mp="1q4d56he84h5">
            +11 % vs mois précédent
          </div>
        </div>
        <div className="border border-slate-200 rounded-xl shadow-sm p-5 flex flex-col gap-2 bg-white" data-id="element-1164" data-mp="3169dkzefd08j">
          <div className="text-sm font-medium text-slate-500 font-body" data-id="element-1165" data-mp="357mm5h8wjhj5">
            Rappels actifs
          </div>
          <div className="text-3xl font-bold text-[#0F172A] font-heading" data-id="element-1166" data-mp="3a4cxjr9rhoy8">
            19 204
          </div>
          <div className="text-sm text-emerald-600 font-medium font-body" data-id="element-1167" data-mp="3exxt7cnvu6u9">
            +7 % vs mois précédent
          </div>
        </div>
        <div className="border border-slate-200 rounded-xl shadow-sm p-5 flex flex-col gap-2 bg-white" data-id="element-1168" data-mp="2mg23b6ykmnix">
          <div className="text-sm font-medium text-slate-500 font-body" data-id="element-1169" data-mp="3pqpozjn94k9i">
            Adresses uniques avec rappels
          </div>
          <div className="text-3xl font-bold text-[#0F172A] font-heading" data-id="element-1170" data-mp="3krd5n8vl9vbo">
            14 882
          </div>
          <div className="text-sm text-slate-500 font-body" data-id="element-1171" data-mp="2dnc1worfetv">
            Couverture rappel : 38 %
          </div>
        </div>
        <div className="border border-slate-200 rounded-xl shadow-sm p-5 flex flex-col gap-2 bg-white" data-id="element-1172" data-mp="3ukbw6wanbvxx">
          <div className="text-sm font-medium text-slate-500 font-body" data-id="element-1173" data-mp="31taasdgd145y">
            Questions posées à ATNA
          </div>
          <div className="text-3xl font-bold text-[#0F172A] font-heading" data-id="element-1174" data-mp="3ftl55qgvp0vv">
            3 418
          </div>
          <div className="text-sm text-slate-500 font-body" data-id="element-1175" data-mp="3atp6m6qya1np">
            Collectes et matières résiduelles
          </div>
        </div>
        <div className="border border-slate-200 rounded-xl shadow-sm p-5 flex flex-col gap-2 bg-white" data-id="element-1176" data-mp="v0zrzo0f0oe7">
          <div className="text-sm font-medium text-slate-500 font-body" data-id="element-1177" data-mp="q5u6gqaxdbqn">
            Satisfaction ATNA
          </div>
          <div className="text-3xl font-bold text-[#0F172A] font-heading" data-id="element-1178" data-mp="12mddgmgz4sem">
            92 %
          </div>
          <div className="text-sm text-emerald-600 font-medium font-body" data-id="element-1179" data-mp="omu63vvzll3d">
            feedback positif
          </div>
        </div>
        <div className="border border-slate-200 rounded-xl shadow-sm p-5 flex flex-col gap-2 bg-white" data-id="element-1180" data-mp="tmq9bswv18hv">
          <div className="text-sm font-medium text-slate-500 font-body" data-id="element-1181" data-mp="1hckmkbl3cz2n">
            Appels évités estimés
          </div>
          <div className="text-3xl font-bold text-[#0F172A] font-heading" data-id="element-1182" data-mp="1m9jdkl9gkpwm">
            1 247
          </div>
          <div className="text-sm text-slate-500 font-body" data-id="element-1183" data-mp="17jc4ux996l94">
            via rappels et réponses ATNA
          </div>
        </div>
      </section>

      {/* SECTION 2 — USAGE DES CALENDRIERS */}
      <section className="border border-slate-200 rounded-xl shadow-sm bg-white p-6 flex flex-col gap-6" data-id="element-1184" data-mp="2080ykb7zboau">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-1185" data-mp="252x0c330pq15">
          <div data-id="element-1186" data-mp="50nsom3k1zld">
            <h3 className="text-lg font-bold text-[#0F172A] font-heading flex items-center gap-2" data-id="element-1187" data-mp="50fbwj4baa4w">
              <CalendarDays className="w-5 h-5 text-[#4F46E5]" data-id="element-1188" data-mp="61i2l5asb2c" /> Usage des
              calendriers de collecte
            </h3>
            <p className="text-sm text-slate-500 mt-1" data-id="element-1189" data-mp="3nd3qjfr07jzp">
              Collect - Calendar download ICS & PDF / Calendar display
            </p>
          </div>
          <button className="inline-flex items-center gap-2 px-3 py-1.5 border border-slate-200 text-slate-600 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors" data-id="element-1190" data-mp="nvch9y701xed">
            <Filter className="w-4 h-4" data-id="element-1191" data-mp="euxko2ol4vvk" /> Filtres
          </button>
        </div>

        <div className="h-72 w-full" data-id="element-1192" data-mp="9ww17gn8yujk">
          <ResponsiveContainer width="100%" height="100%" data-id="element-1193" data-mp="a00d706yeuks">
            <LineChart data={monthlyData} margin={{
            top: 5,
            right: 20,
            bottom: 5,
            left: 0
          }} data-id="element-1194" data-mp="2znr8rrhm23ls">
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" data-id="element-1195" data-mp="1a7p17euoj9kn" />
              <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{
              fill: '#64748B',
              fontSize: 12
            }} dy={10} data-id="element-1196" data-mp="1j60ob6wc7p8c" />
              <YAxis yAxisId="left" axisLine={false} tickLine={false} tick={{
              fill: '#64748B',
              fontSize: 12
            }} data-id="element-1197" data-mp="1syg9se6uevxz" />
              <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} tick={{
              fill: '#64748B',
              fontSize: 12
            }} data-id="element-1198" data-mp="26wekn9lyga8q" />
              <Tooltip contentStyle={{
              borderRadius: '8px',
              border: '1px solid #E2E8F0',
              boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
            }} data-id="element-1199" data-mp="1r713rodi69u7" />
              <Legend wrapperStyle={{
              paddingTop: '20px'
            }} data-id="element-1200" data-mp="206o7whxm9tqy" />
              <Line yAxisId="right" type="monotone" dataKey="displays" name="Consultations (Display)" stroke="#94A3B8" strokeWidth={2} dot={{
              r: 4
            }} activeDot={{
              r: 6
            }} data-id="element-1201" data-mp="254fxv6epb5si" />
              <Line yAxisId="left" type="monotone" dataKey="downloads" name="Téléchargements (Total)" stroke="#4F46E5" strokeWidth={3} dot={{
              r: 4
            }} activeDot={{
              r: 6
            }} data-id="element-1202" data-mp="2nw8qgzpuyozp" />
              <Line yAxisId="left" type="monotone" dataKey="uniqueDownloads" name="Téléchargements (Adresses uniques)" stroke="#818CF8" strokeWidth={2} strokeDasharray="5 5" dot={{
              r: 4
            }} data-id="element-1203" data-mp="h7yofp7zc1ca" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-indigo-50/50 rounded-lg p-4 flex items-start gap-3" data-id="element-1204" data-mp="101lrg2ws7u9b">
          <Lightbulb className="w-5 h-5 text-[#4F46E5] shrink-0 mt-0.5" data-id="element-1205" data-mp="v6pzt3vgj8wq" />
          <p className="text-sm text-slate-700" data-id="element-1206" data-mp="q8oesdeh0zim">
            <span className="font-semibold text-[#0F172A]" data-id="element-1207" data-mp="1notu0d2tsp7n">Insight :</span> Les
            téléchargements augmentent significativement avant les changements
            de saison (avril/mai) et les jours fériés. Taux de conversion
            consultation → téléchargement : 30%.
          </p>
        </div>
      </section>

      {/* SECTION 3 — RAPPELS CITOYENS */}
      <section className="border border-slate-200 rounded-xl shadow-sm bg-white p-6 flex flex-col gap-6" data-id="element-1208" data-mp="3i4iq3vb1clww">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-1209" data-mp="33fw5be2n31ya">
          <div data-id="element-1210" data-mp="38do32opytiae">
            <h3 className="text-lg font-bold text-[#0F172A] font-heading flex items-center gap-2" data-id="element-1211" data-mp="38d60798ss5ti">
              <BellRing className="w-5 h-5 text-[#4F46E5]" data-id="element-1212" data-mp="2uhkd57m4jw3q" /> Rappels actifs
            </h3>
            <p className="text-sm text-slate-500 mt-1" data-id="element-1213" data-mp="2yeymcz9t23vq">
              Collect - Active reminders / by address unique
            </p>
          </div>
          <button className="inline-flex items-center gap-2 px-4 py-2 bg-[#4F46E5] text-white text-sm font-medium rounded-lg hover:bg-[#4338CA] transition-colors shadow-sm" data-id="element-1214" data-mp="2pflbc261978w">
            <Send className="w-4 h-4" data-id="element-1215" data-mp="np02ep1kqz3n" /> Préparer une campagne d'activation
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-2" data-id="element-1216" data-mp="286sstuzm8t26">
          <div className="flex flex-col gap-1" data-id="element-1217" data-mp="284y92k02z6zo">
            <div className="text-sm text-slate-500" data-id="element-1218" data-mp="238r0skk7pjek">Taux d'activation</div>
            <div className="text-xl font-bold text-[#0F172A]" data-id="element-1219" data-mp="2hwlti0y7znrn">38 %</div>
          </div>
          <div className="flex flex-col gap-1" data-id="element-1220" data-mp="2hyoqj4xsih5v">
            <div className="text-sm text-slate-500" data-id="element-1221" data-mp="2d541i2e6cxit">Taux d'ouverture</div>
            <div className="text-xl font-bold text-[#0F172A]" data-id="element-1222" data-mp="2qx0r9zsdevc1">76 %</div>
          </div>
          <div className="flex flex-col gap-1" data-id="element-1223" data-mp="2lypi7r9wgouv">
            <div className="text-sm text-slate-500" data-id="element-1224" data-mp="2lxxlgd4kgl30">Taux de désabonnement</div>
            <div className="text-xl font-bold text-[#0F172A]" data-id="element-1225" data-mp="30uonku5mkawb">1.2 %</div>
          </div>
          <div className="flex flex-col gap-1" data-id="element-1226" data-mp="2vwde4kj8s2em">
            <div className="text-sm text-slate-500" data-id="element-1227" data-mp="2vuk6b1ww7c51">Rappels envoyés (Juin)</div>
            <div className="text-xl font-bold text-[#0F172A]" data-id="element-1228" data-mp="16i8ku3c1few7">76 816</div>
          </div>
          <div className="flex flex-col gap-1" data-id="element-1229" data-mp="11jxazslqt4dz">
            <div className="text-sm text-slate-500" data-id="element-1230" data-mp="3or1sfiph7lsa">Canaux favoris</div>
            <div className="text-sm font-bold text-[#0F172A]" data-id="element-1231" data-mp="3oqtf5ppfyegk">
              Push (65%), Email (25%)
            </div>
          </div>
        </div>

        <div className="h-64 w-full" data-id="element-1232" data-mp="2jbnpawlkkk4">
          <ResponsiveContainer width="100%" height="100%" data-id="element-1233" data-mp="2kcwf8q54uyo">
            <LineChart data={monthlyData} margin={{
            top: 5,
            right: 20,
            bottom: 5,
            left: 0
          }} data-id="element-1234" data-mp="6lygpf508biw">
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" data-id="element-1235" data-mp="3b0pxlc1ib8xg" />
              <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{
              fill: '#64748B',
              fontSize: 12
            }} dy={10} data-id="element-1236" data-mp="3ezf7gunc68cc" />
              <YAxis axisLine={false} tickLine={false} tick={{
              fill: '#64748B',
              fontSize: 12
            }} data-id="element-1237" data-mp="2ra4fa1k1xm9t" />
              <Tooltip contentStyle={{
              borderRadius: '8px',
              border: '1px solid #E2E8F0'
            }} data-id="element-1238" data-mp="2etuxhqr848qh" />
              <Legend wrapperStyle={{
              paddingTop: '20px'
            }} data-id="element-1239" data-mp="25rlox6fimpfq" />
              <Line type="stepAfter" dataKey="reminders" name="Rappels actifs (Total)" stroke="#10B981" strokeWidth={3} dot={{
              r: 4
            }} activeDot={{
              r: 6
            }} data-id="element-1240" data-mp="20ulmd5hvk4td" />
              <Line type="stepAfter" dataKey="uniqueReminders" name="Rappels actifs (Adresses uniques)" stroke="#34D399" strokeWidth={2} strokeDasharray="5 5" dot={{
              r: 4
            }} data-id="element-1241" data-mp="1i32ikwg10mqa" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* SECTION 4 — QUESTIONS ATNA */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1242" data-mp="tkajnwib4p0n">
        <div className="lg:col-span-2 border border-slate-200 rounded-xl shadow-sm bg-white p-6 flex flex-col gap-6" data-id="element-1243" data-mp="yhj1fvzjmr1d">
          <div data-id="element-1244" data-mp="1m8ogi8xntfam">
            <h3 className="text-lg font-bold text-[#0F172A] font-heading flex items-center gap-2" data-id="element-1245" data-mp="1r5onqn68wo3b">
              <MessageSquare className="w-5 h-5 text-[#4F46E5]" data-id="element-1246" data-mp="1cgs7w3xy66s5" /> Questions
              citoyennes posées à ATNA
            </h3>
            <p className="text-sm text-slate-500 mt-1" data-id="element-1247" data-mp="1hf3fe80rs577">
              Collect - Questions asked / feedback positive and negative
            </p>
          </div>

          <div className="grid grid-cols-3 gap-4 mb-2" data-id="element-1248" data-mp="253mnonjtkhza">
            <div className="flex flex-col gap-1" data-id="element-1249" data-mp="29wxxmxjxhevf">
              <div className="text-sm text-slate-500" data-id="element-1250" data-mp="3uml7ltjci38e">Réponses vérifiées</div>
              <div className="text-xl font-bold text-emerald-600" data-id="element-1251" data-mp="3uki7uhse4vqh">88 %</div>
            </div>
            <div className="flex flex-col gap-1" data-id="element-1252" data-mp="3ple0lwp1182s">
              <div className="text-sm text-slate-500" data-id="element-1253" data-mp="879x8kwv2ob4">
                Sous seuil de confiance
              </div>
              <div className="text-xl font-bold text-amber-600" data-id="element-1254" data-mp="38yral9osxy6">9 %</div>
            </div>
            <div className="flex flex-col gap-1" data-id="element-1255" data-mp="3bmxm3jkxada3">
              <div className="text-sm text-slate-500" data-id="element-1256" data-mp="3bpk287lvq13x">
                Sans réponse officielle
              </div>
              <div className="text-xl font-bold text-red-600" data-id="element-1257" data-mp="3kof8fjoxbb78">3 %</div>
            </div>
          </div>

          <div className="h-64 w-full" data-id="element-1258" data-mp="3gn21t7e0b3z9">
            <ResponsiveContainer width="100%" height="100%" data-id="element-1259" data-mp="3gm0qr2wzqhhj">
              <BarChart data={monthlyData} margin={{
              top: 5,
              right: 0,
              bottom: 5,
              left: -20
            }} data-id="element-1260" data-mp="zxwkw05paptr">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" data-id="element-1261" data-mp="2pgmpl7lmstoi" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{
                fill: '#64748B',
                fontSize: 12
              }} dy={10} data-id="element-1262" data-mp="2zdrehf6v3390" />
                <YAxis yAxisId="left" axisLine={false} tickLine={false} tick={{
                fill: '#64748B',
                fontSize: 12
              }} data-id="element-1263" data-mp="3d4w6pw7wgt9b" />
                <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} tick={{
                fill: '#64748B',
                fontSize: 12
              }} data-id="element-1264" data-mp="3n2k2mph8s6t2" />
                <Tooltip contentStyle={{
                borderRadius: '8px',
                border: '1px solid #E2E8F0'
              }} cursor={{
                fill: '#F8FAFC'
              }} data-id="element-1265" data-mp="9vhk9i28mlgw" />
                <Legend wrapperStyle={{
                paddingTop: '20px'
              }} data-id="element-1266" data-mp="3s8yymvph1kkc" />
                <Bar yAxisId="left" dataKey="posFeedback" name="Feedback Positif" stackId="a" fill="#10B981" radius={[0, 0, 4, 4]} data-id="element-1267" data-mp="srzjekjf1nn6" />
                <Bar yAxisId="left" dataKey="negFeedback" name="Feedback Négatif" stackId="a" fill="#F43F5E" radius={[4, 4, 0, 0]} data-id="element-1268" data-mp="etsv9w49r20p" />
                <Line yAxisId="right" type="monotone" dataKey="questions" name="Total Questions" stroke="#0F172A" strokeWidth={2} dot={{
                r: 4
              }} data-id="element-1269" data-mp="2zll8bgga3n97" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="border border-slate-200 rounded-xl shadow-sm bg-white p-6 flex flex-col gap-4" data-id="element-1270" data-mp="3u57zykdtjk56">
          <h3 className="text-base font-bold text-[#0F172A] font-heading" data-id="element-1271" data-mp="3p6ejcj38jd0s">
            Top 10 questions
          </h3>
          <ul className="space-y-3" data-id="element-1272" data-mp="3k68x5ifqiiq3">
            {['Quand est ma prochaine collecte ?', "Est-ce que la collecte change lors d'un jour férié ?", 'Où mettre les encombrants ?', 'Quand sortir mon bac ?', 'Comment disposer des branches ?', 'Est-ce que le compost est collecté cette semaine ?', "Que faire si mon bac n'a pas été ramassé ?", 'Où va le recyclage ?', 'Comment demander un bac ?', 'Comment signaler une collecte manquée ?'].map((q, i) => <li key={i} className="flex items-start gap-3 text-sm" data-id="element-1273" data-mp="t7w51a4n1dxg" data-mpi={i}>
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center text-xs font-bold" data-id="element-1274" data-mp="xzdw96gv0058">
                  {i + 1}
                </span>
                <span className="text-slate-700 pt-0.5" data-id="element-1275" data-mp="170293xuinugj">{q}</span>
              </li>)}
          </ul>
        </div>
      </section>

      {/* SECTION 5 — HEAT MAP TERRITORIALE */}
      <section className="flex flex-col gap-4" data-id="element-1276" data-mp="1gyi0m1xgh5qk">
        <div className="flex flex-col gap-1" data-id="element-1277" data-mp="1gx6zk9veac35">
          <h2 className="text-lg font-bold text-[#0F172A] font-heading" data-id="element-1278" data-mp="3dqryx23sozcj">
            Couverture numérique des collectes
          </h2>
          <p className="text-sm text-slate-500 font-body" data-id="element-1279" data-mp="3ip3b3jhkblxx">
            Sélectionnez un secteur pour voir l'adoption des calendriers,
            rappels et questions ATNA.
          </p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6" data-id="element-1280" data-mp="36odefutpgsxm">
          <div className="lg:col-span-3" data-id="element-1281" data-mp="3knczytlx8rpk">
            <CollectesHeatMap districts={collectesDistricts} selectedId={selectedDistrictId} onSelect={setSelectedDistrictId} data-id="element-1282" data-mp="3koea8vv45a68" />
          </div>
          <div className="lg:col-span-2" data-id="element-1283" data-mp="3tmzssr0l4p70">
            <div className="border border-slate-200 rounded-xl shadow-sm bg-white flex flex-col h-full overflow-hidden font-body" data-id="element-1284" data-mp="3tlyiiore86qc">
              <div className="px-5 py-4 border-b border-slate-100 flex items-start justify-between gap-4" data-id="element-1285" data-mp="78zxieczy4g5">
                <div className="flex flex-col gap-1" data-id="element-1286" data-mp="293rk9kqo2xy">
                  <div className="flex items-center gap-2 text-xs text-slate-500 uppercase tracking-wide" data-id="element-1287" data-mp="2bghpa0nhjm3">
                    <MapPin className="w-3.5 h-3.5" data-id="element-1288" data-mp="h49wsuc0fxq8" /> Secteur sélectionné
                  </div>
                  <div className="flex items-center gap-3" data-id="element-1289" data-mp="c5qd70kw2y11">
                    <h3 className="text-xl font-bold text-[#0F172A] font-heading" data-id="element-1290" data-mp="vuo4vwk6w98h">
                      {selectedDistrict.name}
                    </h3>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${getStatusBadge(getAdoptionStatus(selectedDistrict.adoption))}`} data-id="element-1291" data-mp="lvfetesg3ere">
                      {getStatusLabel(getAdoptionStatus(selectedDistrict.adoption))}
                    </span>
                  </div>
                </div>
              </div>

              <div className="px-5 py-4 border-b border-slate-100 flex flex-col gap-4" data-id="element-1292" data-mp="368rtfkmc0zs">
                <div className="grid grid-cols-2 gap-4" data-id="element-1293" data-mp="84k1nqawybi0">
                  <div className="flex flex-col gap-0.5" data-id="element-1294" data-mp="3pc5p45v28dyw">
                    <div className="text-lg font-bold text-[#0F172A] font-heading" data-id="element-1295" data-mp="3pcnubrzpa2iy">
                      {selectedDistrict.addresses.toLocaleString('fr-CA')}
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-1296" data-mp="11f72udpznm2t">
                      Adresses couvertes
                    </div>
                  </div>
                  <div className="flex flex-col gap-0.5" data-id="element-1297" data-mp="rqp19u9vxay4">
                    <div className="text-lg font-bold text-[#0F172A] font-heading" data-id="element-1298" data-mp="rp46c3jnrpzq">
                      {selectedDistrict.calendars.toLocaleString('fr-CA')}
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-1299" data-mp="htan61bfoy8z">
                      Calendriers consultés
                    </div>
                  </div>
                  <div className="flex flex-col gap-0.5" data-id="element-1300" data-mp="8rkxp11449du">
                    <div className="text-lg font-bold text-[#0F172A] font-heading" data-id="element-1301" data-mp="8s32wn5r5xxw">
                      {selectedDistrict.reminders.toLocaleString('fr-CA')}
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-1302" data-mp="234j7zkmtb616">Rappels actifs</div>
                  </div>
                  <div className="flex flex-col gap-0.5" data-id="element-1303" data-mp="1y7alj7umsfu4">
                    <div className="text-lg font-bold text-[#0F172A] font-heading" data-id="element-1304" data-mp="25qtmuu7qsvri">
                      {selectedDistrict.openRate} %
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-1305" data-mp="2anaalnb15842">
                      Taux d'ouverture
                    </div>
                  </div>
                </div>
              </div>

              <div className="px-5 py-4 flex flex-col gap-4 bg-slate-50 flex-1" data-id="element-1306" data-mp="2keen8gd8ggvq">
                <div className="flex items-center justify-between" data-id="element-1307" data-mp="1mspngrhnqb2w">
                  <div className="flex flex-col" data-id="element-1308" data-mp="1rpzc44x82ipo">
                    <span className="text-lg font-bold text-[#0F172A]" data-id="element-1309" data-mp="1rqr5ra79e6db">
                      {selectedDistrict.atnaQuestions.toLocaleString('fr-CA')}
                    </span>
                    <span className="text-xs text-slate-500 uppercase tracking-wide" data-id="element-1310" data-mp="21pgiypo7n2n8">
                      Questions ATNA
                    </span>
                  </div>
                  <div className="flex flex-col items-end" data-id="element-1311" data-mp="3d32gay484aju">
                    <span className="text-lg font-bold text-red-600" data-id="element-1312" data-mp="3d5onuxr46q22">
                      {selectedDistrict.complaints}
                    </span>
                    <span className="text-xs text-slate-500 uppercase tracking-wide" data-id="element-1313" data-mp="181g85tkyvej0">
                      Signalements collecte manquée
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 6 & 7 — PERFORMANCE & IMPACT */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1314" data-mp="u90cjdueais4">
        {/* Performance Opérationnelle */}
        <div className="border border-slate-200 rounded-xl shadow-sm bg-white p-6 flex flex-col gap-6" data-id="element-1315" data-mp="pa6x3fvjshpb">
          <h3 className="text-lg font-bold text-[#0F172A] font-heading flex items-center gap-2" data-id="element-1316" data-mp="ycpwtr41ee7c">
            <TrendingUp className="w-5 h-5 text-[#4F46E5]" data-id="element-1317" data-mp="an3z783bc71g" /> Performance des
            collectes
          </h3>

          <div className="grid grid-cols-3 gap-4" data-id="element-1318" data-mp="6ls44n1o6tlv">
            <div className="flex flex-col gap-1" data-id="element-1319" data-mp="6l0jg79m2gad">
              <div className="text-sm text-slate-500" data-id="element-1320" data-mp="3smg1newr3y7z">Taux de réussite</div>
              <div className="text-xl font-bold text-emerald-600" data-id="element-1321" data-mp="3sl50znylr6l3">98.4 %</div>
            </div>
            <div className="flex flex-col gap-1" data-id="element-1322" data-mp="1a4y2vyinb10">
              <div className="text-sm text-slate-500" data-id="element-1323" data-mp="3ipuuoo168d1i">Collectes prévues</div>
              <div className="text-xl font-bold text-[#0F172A]" data-id="element-1324" data-mp="3nnd8osu1pigd">184</div>
            </div>
            <div className="flex flex-col gap-1" data-id="element-1325" data-mp="f8cpkocs9k4s">
              <div className="text-sm text-slate-500" data-id="element-1326" data-mp="f8mfkbxtvrah">Signalements</div>
              <div className="text-xl font-bold text-amber-600" data-id="element-1327" data-mp="k64rmb756mmp">42</div>
            </div>
          </div>

          <div className="h-52 w-full mt-2" data-id="element-1328" data-mp="58vmmeox1kci">
            <ResponsiveContainer width="100%" height="100%" data-id="element-1329" data-mp="5c9mnga3xhgs">
              <BarChart data={perfData} layout="vertical" margin={{
              top: 0,
              right: 20,
              bottom: 0,
              left: 20
            }} data-id="element-1330" data-mp="a7owzy8wov8g">
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#E2E8F0" data-id="element-1331" data-mp="2c97sv1il2lwg" />
                <XAxis type="number" domain={[90, 100]} axisLine={false} tickLine={false} tick={{
                fill: '#64748B',
                fontSize: 12
              }} data-id="element-1332" data-mp="27bofs4xmgf63" />
                <YAxis type="category" dataKey="name" axisLine={false} tickLine={false} tick={{
                fill: '#0F172A',
                fontSize: 12,
                fontWeight: 500
              }} data-id="element-1333" data-mp="2v1adenycimbz" />
                <Tooltip cursor={{
                fill: '#F8FAFC'
              }} contentStyle={{
                borderRadius: '8px',
                border: '1px solid #E2E8F0'
              }} data-id="element-1334" data-mp="3dpfetorj5v7m" />
                <Bar dataKey="reussite" name="Taux de réussite (%)" fill="#4F46E5" radius={[0, 4, 4, 0]} barSize={20} data-id="element-1335" data-mp="vinsbl0h7lwu" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Impact Environnemental */}
        <div className="border border-slate-200 rounded-xl shadow-sm bg-white p-6 flex flex-col gap-6" data-id="element-1336" data-mp="1ee45vp30ij75">
          <h3 className="text-lg font-bold text-[#0F172A] font-heading flex items-center gap-2" data-id="element-1337" data-mp="1jd8fggtumiy0">
            <Leaf className="w-5 h-5 text-emerald-600" data-id="element-1338" data-mp="1o61kl5t8btam" /> Performance
            environnementale
          </h3>

          <div className="grid grid-cols-3 gap-4" data-id="element-1339" data-mp="1s627zfyb3ett">
            <div className="flex flex-col gap-1" data-id="element-1340" data-mp="1x5wz9klzu6fe">
              <div className="text-sm text-slate-500" data-id="element-1341" data-mp="1x782njbj1c5z">Taux de diversion</div>
              <div className="text-xl font-bold text-emerald-600" data-id="element-1342" data-mp="225t0xd77xlrs">60.5 %</div>
            </div>
            <div className="flex flex-col gap-1" data-id="element-1343" data-mp="32euj6qepclhx">
              <div className="text-sm text-slate-500" data-id="element-1344" data-mp="32i8mq1z3r0qy">Déchets ultimes</div>
              <div className="text-xl font-bold text-red-500" data-id="element-1345" data-mp="37gjso1ma0r3w">-4 %</div>
            </div>
            <div className="flex flex-col gap-1" data-id="element-1346" data-mp="2tgis6n9xbloy">
              <div className="text-sm text-slate-500" data-id="element-1347" data-mp="2ye0v9x19f6p1">CO2 évité estimé</div>
              <div className="text-xl font-bold text-[#0F172A]" data-id="element-1348" data-mp="2yger483rkmxn">842 t</div>
            </div>
          </div>

          <div className="h-52 w-full mt-2" data-id="element-1349" data-mp="3m691mj0m1zeq">
            <ResponsiveContainer width="100%" height="100%" data-id="element-1350" data-mp="3q4f4hfybvuud">
              <BarChart data={envData} margin={{
              top: 0,
              right: 0,
              bottom: 0,
              left: -10
            }} data-id="element-1351" data-mp="3ce38v72jbdyh">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" data-id="element-1352" data-mp="8x34ohbr7ng0" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{
                fill: '#64748B',
                fontSize: 12
              }} dy={10} data-id="element-1353" data-mp="dpwevkpzrnzh" />
                <YAxis axisLine={false} tickLine={false} tick={{
                fill: '#64748B',
                fontSize: 12
              }} data-id="element-1354" data-mp="wuspmz65f3wj" />
                <Tooltip cursor={{
                fill: '#F8FAFC'
              }} contentStyle={{
                borderRadius: '8px',
                border: '1px solid #E2E8F0'
              }} data-id="element-1355" data-mp="2p7c65f4c5r" />
                <Legend wrapperStyle={{
                paddingTop: '10px'
              }} data-id="element-1356" data-mp="95gl4clrnk0k" />
                <Bar dataKey="dechets" name="Déchets (t)" stackId="a" fill="#94A3B8" data-id="element-1357" data-mp="1kiskobahtggx" />
                <Bar dataKey="recyclage" name="Recyclage (t)" stackId="a" fill="#3B82F6" data-id="element-1358" data-mp="1wkjzeods9w8g" />
                <Bar dataKey="compost" name="Compost (t)" stackId="a" fill="#10B981" radius={[4, 4, 0, 0]} data-id="element-1359" data-mp="21hk6n2mdd515" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      {/* SECTION 8 — RECOMMANDATIONS ATNA */}
      <section className="border border-indigo-200 rounded-xl shadow-sm bg-indigo-50/30 p-6 flex flex-col gap-6" data-id="element-1360" data-mp="1is43e7jauyh1">
        <div className="flex items-center gap-2" data-id="element-1361" data-mp="1nlgfji6slcsw">
          <Sparkles className="w-6 h-6 text-[#4F46E5]" data-id="element-1362" data-mp="q4rg1v5bemmo" />
          <h2 className="text-xl font-bold text-[#0F172A] font-heading" data-id="element-1363" data-mp="q480um71uccc">
            Recommandations ATNA
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1364" data-mp="njs5cmhqs6nz">
          <div className="bg-white rounded-lg p-5 border border-slate-200 shadow-sm flex flex-col h-full" data-id="element-1365" data-mp="njaa9t6tfyhn">
            <div className="flex items-center gap-2 mb-3" data-id="element-1366" data-mp="iivzw4hqscgg">
              <AlertTriangle className="w-4 h-4 text-amber-500" data-id="element-1367" data-mp="dkulvxz6b3bu" />
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wide" data-id="element-1368" data-mp="8n1dqzeb7z8x">
                Adoption
              </span>
            </div>
            <p className="text-sm font-bold text-[#0F172A] mb-2" data-id="element-1369" data-mp="4mr153w0ial3">
              Le secteur B a un faible taux de rappels actifs.
            </p>
            <p className="text-sm text-slate-600 mb-6 flex-1" data-id="element-1370" data-mp="3r0iq67hzgo9q">
              Une campagne ciblée pourrait augmenter la couverture de 42 % à 58
              %.
            </p>
            <button className="w-full inline-flex justify-center items-center gap-2 px-4 py-2 bg-[#4F46E5] text-white text-sm font-medium rounded-lg hover:bg-[#4338CA] transition-colors" data-id="element-1371" data-mp="3lzupsv7v6v2u">
              <Send className="w-4 h-4" data-id="element-1372" data-mp="3d33yew49c4el" /> Préparer l'avis citoyen
            </button>
          </div>

          <div className="bg-white rounded-lg p-5 border border-slate-200 shadow-sm flex flex-col h-full" data-id="element-1373" data-mp="5ocb56n3ssce">
            <div className="flex items-center gap-2 mb-3" data-id="element-1374" data-mp="an6uc9ozgix1">
              <MessageSquare className="w-4 h-4 text-blue-500" data-id="element-1375" data-mp="3syk5l7m56n19" />
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wide" data-id="element-1376" data-mp="v12r12n83id">
                Connaissances
              </span>
            </div>
            <p className="text-sm font-bold text-[#0F172A] mb-2" data-id="element-1377" data-mp="3izumxcmf8yjy">
              Les questions sur les branches ont augmenté de 126 % cette
              semaine.
            </p>
            <p className="text-sm text-slate-600 mb-6 flex-1" data-id="element-1378" data-mp="3a1slivys03w5">
              Créer une FAQ officielle ou envoyer une notification ciblée.
            </p>
            <button className="w-full inline-flex justify-center items-center gap-2 px-4 py-2 border border-[#4F46E5] text-[#4F46E5] text-sm font-medium rounded-lg hover:bg-indigo-50 transition-colors" data-id="element-1379" data-mp="189dxdf5n237a">
              Créer la réponse officielle
            </button>
          </div>

          <div className="bg-white rounded-lg p-5 border border-slate-200 shadow-sm flex flex-col h-full" data-id="element-1380" data-mp="2wqdzdfuxy300">
            <div className="flex items-center gap-2 mb-3" data-id="element-1381" data-mp="36ijtav4r6uhu">
              <AlertTriangle className="w-4 h-4 text-red-500" data-id="element-1382" data-mp="36k4magbf65dl" />
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wide" data-id="element-1383" data-mp="31rl9hlkk3e9u">
                Opérations
              </span>
            </div>
            <p className="text-sm font-bold text-[#0F172A] mb-2" data-id="element-1384" data-mp="3giu5qdiyrxvy">
              18 signalements de collecte manquée proviennent du secteur B.
            </p>
            <p className="text-sm text-slate-600 mb-6 flex-1" data-id="element-1385" data-mp="3pgcwlyjah6fr">
              Vérifier le trajet ou envoyer un avis proactif aux résidents.
            </p>
            <button className="w-full inline-flex justify-center items-center gap-2 px-4 py-2 border border-slate-300 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors" data-id="element-1386" data-mp="1v3eonll2alvl">
              Créer une requête opérationnelle
            </button>
          </div>
        </div>
      </section>

      {/* SECTION 9 — VALEUR CRÉÉE */}
      <section className="bg-[#0F172A] rounded-xl shadow-lg p-8 text-white relative overflow-hidden" data-id="element-1387" data-mp="3v6fjpq3yx3qk">
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#4F46E5] rounded-full blur-3xl opacity-20 -translate-y-1/2 translate-x-1/3" data-id="element-1388" data-mp="3xrwt5q2x8gw"></div>

        <div className="relative z-10" data-id="element-1389" data-mp="d015dq1serrn">
          <h2 className="text-2xl font-bold font-heading mb-2" data-id="element-1390" data-mp="cyzxfug2ile5">
            Valeur générée par Collectes
          </h2>
          <p className="text-slate-300 text-sm max-w-2xl mb-8" data-id="element-1391" data-mp="hxjf3inmpb0p">
            Collectes ne mesure pas seulement l'usage. Il transforme les données
            citoyennes en actions opérationnelles.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-6" data-id="element-1392" data-mp="rr0c0vj2b68l">
            <div className="flex flex-col gap-1" data-id="element-1393" data-mp="vrkbi5r3ylxw">
              <div className="text-3xl font-bold text-white font-heading" data-id="element-1394" data-mp="10plqofley143">
                1 247
              </div>
              <div className="text-sm text-slate-400" data-id="element-1395" data-mp="2l7ed7agc3axc">
                Appels évités estimés
              </div>
            </div>
            <div className="flex flex-col gap-1" data-id="element-1396" data-mp="2g7if1rubidpp">
              <div className="text-3xl font-bold text-white font-heading" data-id="element-1397" data-mp="2c6yesfeg6tzc">
                850+
              </div>
              <div className="text-sm text-slate-400" data-id="element-1398" data-mp="278538siq1d1t">Courriels évités</div>
            </div>
            <div className="flex flex-col gap-1" data-id="element-1399" data-mp="33xw73icwkcka">
              <div className="text-3xl font-bold text-white font-heading" data-id="element-1400" data-mp="33xmgbsk1a1dj">
                140 h
              </div>
              <div className="text-sm text-slate-400" data-id="element-1401" data-mp="2zynckba1gano">
                Temps admin économisé
              </div>
            </div>
            <div className="flex flex-col gap-1" data-id="element-1402" data-mp="2q6zehriz7vd8">
              <div className="text-3xl font-bold text-emerald-400 font-heading" data-id="element-1403" data-mp="2q67u7cutxk29">
                +42 %
              </div>
              <div className="text-sm text-slate-400" data-id="element-1404" data-mp="3no7typqjmzuu">
                Signalements structurés
              </div>
            </div>
            <div className="flex flex-col gap-1" data-id="element-1405" data-mp="2r7bs1gy2lude">
              <div className="text-3xl font-bold text-emerald-400 font-heading" data-id="element-1406" data-mp="2r6iun6l04h7x">
                92 %
              </div>
              <div className="text-sm text-slate-400" data-id="element-1407" data-mp="2w0dce4h1qm4d">
                Satisfaction citoyenne
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>;
}